import React, {useState, useRef, useEffect} from 'react'
import {BankOption} from '../types'

interface SearchableSelectProps {
  options: BankOption[]
  value: string
  placeholder: string
  loadingText: string
  isLoading?: boolean
  disabled?: boolean
  onSelect: (value: string) => void
  className?: string
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  placeholder,
  loadingText,
  isLoading = false,
  disabled = false,
  onSelect,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<BankOption[]>(options)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Update filtered options when options or search term changes
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options)
    } else {
      const searchLower = searchTerm.toLowerCase().trim()
      const filtered = options.filter((option) => {
        const nameLower = option.code_nm.toLowerCase()
        const codeLower = option.code.toLowerCase()

        // Search in both name and code
        return (
          nameLower.includes(searchLower) ||
          codeLower.includes(searchLower) ||
          // Remove Vietnamese accents for better search
          removeVietnameseAccents(nameLower).includes(removeVietnameseAccents(searchLower))
        )
      })
      setFilteredOptions(filtered)
    }
    setHighlightedIndex(-1) // Reset highlighted index when search changes
  }, [options, searchTerm])

  // Helper function to remove Vietnamese accents
  const removeVietnameseAccents = (str: string): string => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleSelect = (selectedValue: string) => {
    onSelect(selectedValue)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleToggle = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen)
    }
  }

  const getDisplayText = () => {
    if (isLoading) return loadingText
    if (!value) return placeholder

    const selectedOption = options.find((option) => option.code === value)
    return selectedOption ? selectedOption.code_nm : placeholder
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex].code)
      } else if (filteredOptions.length === 1) {
        handleSelect(filteredOptions[0].code)
      }
    }
  }

  return (
    <div className={`position-relative ${className}`} ref={dropdownRef}>
      {/* Main Select Button */}
      <button
        type='button'
        className={`form-control form-control-lg form-control-solid d-flex justify-content-between align-items-center ${
          disabled || isLoading ? 'disabled' : ''
        }`}
        onClick={handleToggle}
        disabled={disabled || isLoading}
        style={{textAlign: 'left'}}
      >
        <span className={!value && !isLoading ? 'text-muted' : ''}>{getDisplayText()}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ms-2`}></i>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className='position-absolute w-100 bg-white border rounded shadow-lg'
          style={{
            zIndex: 1050,
            top: '100%',
            maxHeight: '320px',
            overflowY: 'hidden',
            border: '1px solid #e4e6ef',
            boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075)',
          }}
        >
          {/* Search Input */}
          <div className='p-3 border-bottom' style={{backgroundColor: '#f8f9fa'}}>
            <div className='position-relative'>
              <input
                ref={inputRef}
                type='text'
                className='form-control form-control-sm'
                placeholder='Tìm kiếm...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  paddingRight: '2.5rem',
                  border: '1px solid #e4e6ef',
                  borderRadius: '0.475rem',
                }}
              />
              <i className='fas fa-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted'></i>
            </div>
          </div>

          {/* Options List */}
          <div className='overflow-auto' style={{maxHeight: '200px'}}>
            {filteredOptions.length === 0 ? (
              <div className='p-4 text-muted text-center'>
                <i className='fas fa-search me-2 text-primary'></i>
                <div>Không tìm thấy kết quả</div>
                {searchTerm && (
                  <small className='d-block mt-1'>Thử tìm kiếm với từ khóa khác</small>
                )}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.code}
                  type='button'
                  className={`w-100 text-start p-3 border-0 ${
                    value === option.code
                      ? 'bg-primary text-white'
                      : index === highlightedIndex
                      ? 'bg-light'
                      : 'bg-transparent'
                  }`}
                  onClick={() => handleSelect(option.code)}
                  style={{
                    transition: 'all 0.15s ease-in-out',
                    borderBottom: '1px solid #f1f3f6',
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                >
                  <div className='d-flex flex-column'>
                    <span
                      className={`fw-semibold ${
                        value === option.code
                          ? 'text-white'
                          : index === highlightedIndex
                          ? 'text-primary'
                          : 'text-dark'
                      }`}
                    >
                      {option.code_nm}
                    </span>
                    {/* <small
                      className={`${
                        value === option.code
                          ? 'text-white-50'
                          : index === highlightedIndex
                          ? 'text-primary'
                          : 'text-muted'
                      }`}
                    >
                      Mã: {option.code}
                    </small> */}
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Results Count */}
          {searchTerm && filteredOptions.length > 0 && (
            <div className='p-2 border-top text-center' style={{backgroundColor: '#f8f9fa'}}>
              <small className='text-muted'>
                <i className='fas fa-check-circle me-1 text-success'></i>
                {filteredOptions.length} kết quả tìm thấy
                {searchTerm && ` cho "${searchTerm}"`}
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
